import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, KeyRound, ArrowRight, Mail, RotateCw } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";
import InstallAppButton from "@/components/InstallAppButton";

export default function Login() {
    const { login, verifyOtp, resendOtp, error, setError } = useAuth();
    const navigate = useNavigate();
    const [identifier, setIdentifier] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    // OTP step state — admin + staff logins hop into this after password verify
    const [otpCtx, setOtpCtx] = useState(null); // { otp_token, masked_email, role }
    const [otpCode, setOtpCode] = useState("");
    const [otpBusy, setOtpBusy] = useState(false);
    const [otpSecondsLeft, setOtpSecondsLeft] = useState(600);
    const [resendCooldown, setResendCooldown] = useState(0);

    // Countdown ticks — OTP validity + resend cooldown
    useEffect(() => {
        if (!otpCtx) return;
        const t = setInterval(() => {
            setOtpSecondsLeft((s) => (s > 0 ? s - 1 : 0));
            setResendCooldown((s) => (s > 0 ? s - 1 : 0));
        }, 1000);
        return () => clearInterval(t);
    }, [otpCtx]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await login(identifier.trim(), password);
            if (u?.otp_required) {
                setOtpCtx({ otp_token: u.otp_token, masked_email: u.masked_email, role: u.role });
                setOtpSecondsLeft(600);
                setResendCooldown(30);
                setOtpCode("");
                return;
            }
            if (u?.must_change_password) navigate("/change-password", { replace: true });
            else navigate("/", { replace: true });
        } catch (e) {
            console.error("login attempt failed:", e);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        if (!otpCtx || otpCode.length !== 6) return;
        setOtpBusy(true);
        try {
            const u = await verifyOtp(otpCtx.otp_token, otpCode);
            if (u?.must_change_password) navigate("/change-password", { replace: true });
            else navigate("/", { replace: true });
        } catch (e) {
            console.error("otp verify failed:", e);
        } finally {
            setOtpBusy(false);
        }
    };

    const handleResendOtp = async () => {
        if (!otpCtx || resendCooldown > 0) return;
        try {
            await resendOtp(otpCtx.otp_token);
            setOtpSecondsLeft(600);
            setResendCooldown(30);
            setOtpCode("");
            setError("");
        } catch (e) {
            console.error("resend otp failed:", e);
        }
    };

    const cancelOtp = () => {
        setOtpCtx(null);
        setOtpCode("");
        setError("");
    };

    const fmtMMSS = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

    return (
        <div
            className="min-h-screen w-full flex items-center justify-center px-4 py-10 relative"
            style={{
                backgroundImage:
                    "linear-gradient(135deg, rgba(10,17,40,0.85), rgba(10,17,40,0.60)), url('https://images.unsplash.com/photo-1640654694019-b9d3567c0e56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMG5hdnklMjBnb2xkJTIwbHV4dXJ5JTIwdGV4dHVyZXxlbnwwfHx8fDE3ODEwNTQ1OTN8MA&ixlib=rb-4.1.0&q=85')",
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        >
            <div className="w-full max-w-md fade-up">
                {/* Brand */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-3 text-white">
                        <div className="w-14 h-14 flex items-center justify-center bg-[#1A6177] border border-[#C5A059] overflow-hidden">
                            <img src="/icons/mascot-192.png" alt="Tax Solution mascot" className="w-14 h-14 object-cover" />
                        </div>
                        <div className="text-left">
                            <div className="font-heading text-2xl font-bold tracking-tight leading-tight">
                                Tax Solution <span className="text-[#C5A059]">(GTW)</span>
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#C5A059]/80 leading-snug mt-0.5">
                                Powered by<br />
                                <span className="text-white/70 normal-case tracking-wide">Ghatoskash Tax Wizards Pvt&nbsp;Ltd</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="glass-card p-8 md:p-10">
                    <div className="mb-6">
                        <div className="overline mb-3">Secure Sign In</div>
                        <h1 className="font-heading text-3xl font-bold text-[#0A1128] tracking-tight">
                            Welcome back
                        </h1>
                        <p className="text-sm text-[#52525B] mt-2">
                            Login with your User ID, PAN, TAN, or Phone Number
                        </p>
                    </div>

                    {/* Format info card */}
                    <div className="bg-[#FAF3E0]/40 border border-[#C5A059]/30 p-3 mb-6 text-xs text-[#52525B] space-y-1">
                        <div className="overline text-[#C5A059] mb-1">🔐 Accepted Login Formats</div>
                        <div><strong className="text-[#0A1128]">User ID:</strong> e.g. <code className="font-mono">CL-0001</code></div>
                        <div><strong className="text-[#0A1128]">PAN:</strong> 10 chars (e.g. <code className="font-mono">ABCDE1234F</code>)</div>
                        <div><strong className="text-[#0A1128]">TAN:</strong> 10 chars (e.g. <code className="font-mono">BLRT12345C</code>)</div>
                        <div><strong className="text-[#0A1128]">Phone:</strong> 10-digit mobile number</div>
                        <div className="text-[10px] text-[#71717A] mt-1 italic">Email also works for backward compatibility.</div>
                    </div>

                    {otpCtx ? (
                        <form onSubmit={handleVerifyOtp} className="space-y-5" data-testid="login-otp-form">
                            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-900 flex items-start gap-2">
                                <Mail size={14} className="mt-0.5 flex-shrink-0" />
                                <div>
                                    A 6-digit login code has been emailed to <b className="font-mono">{otpCtx.masked_email}</b>. It's valid for 10 minutes.
                                </div>
                            </div>
                            <div>
                                <label className="overline block mb-2" style={{ color: "#0A1128" }}>Enter 6-digit code</label>
                                <input
                                    data-testid="login-otp-input"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={6}
                                    value={otpCode}
                                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    className="input-field font-mono text-center tracking-[12px] text-2xl"
                                    placeholder="••••••"
                                    autoFocus
                                    required
                                />
                                <div className="flex items-center justify-between mt-1.5 text-[11px] text-[#71717A]">
                                    <span>Code expires in <b className="tabular text-[#0A1128]">{fmtMMSS(otpSecondsLeft)}</b></span>
                                    <button
                                        type="button"
                                        onClick={handleResendOtp}
                                        disabled={resendCooldown > 0}
                                        className="inline-flex items-center gap-1 text-[#0A1128] hover:text-[#C5A059] font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                                        data-testid="login-otp-resend"
                                    >
                                        <RotateCw size={11} /> Resend {resendCooldown > 0 ? `(${resendCooldown}s)` : "code"}
                                    </button>
                                </div>
                            </div>
                            {error && (
                                <div data-testid="login-otp-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2">{error}</div>
                            )}
                            <button
                                data-testid="login-otp-submit"
                                type="submit"
                                disabled={otpBusy || otpCode.length !== 6 || otpSecondsLeft <= 0}
                                className="btn-gold w-full flex items-center justify-center gap-2 group"
                            >
                                {otpBusy ? "Verifying…" : "Verify & Sign In"}
                                {!otpBusy && <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />}
                            </button>
                            <button
                                type="button"
                                onClick={cancelOtp}
                                className="w-full text-xs text-[#71717A] hover:text-[#0A1128]"
                                data-testid="login-otp-back"
                            >
                                ← Back to sign in
                            </button>
                        </form>
                    ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="overline block mb-2" style={{ color: "#0A1128" }}>
                                User ID / PAN / TAN / Phone
                            </label>
                            <input
                                data-testid="login-identifier-input"
                                type="text"
                                autoComplete="username"
                                value={identifier}
                                onChange={(e) => setIdentifier(e.target.value)}
                                className="input-field font-mono uppercase tracking-wide"
                                placeholder="e.g. CL-0001 or ABCPD1234E or 98765xxxxx"
                                required
                            />
                        </div>

                        <div>
                            <label className="overline block mb-2" style={{ color: "#0A1128" }}>
                                Password
                            </label>
                            <PasswordInput
                                dataTestId="login-password-input"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Default: Tax@1234"
                                required
                            />
                        </div>

                        {error && (
                            <div
                                data-testid="login-error"
                                className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            data-testid="login-submit-button"
                            type="submit"
                            disabled={loading}
                            className="btn-gold w-full flex items-center justify-center gap-2 group"
                        >
                            {loading ? "Signing in..." : "Sign In"}
                            {!loading && (
                                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                            )}
                        </button>

                        <div className="flex items-center justify-between text-xs pt-2">
                            <Link
                                data-testid="forgot-password-link"
                                to="/forgot-password"
                                className="text-[#0A1128] hover:text-[#C5A059] flex items-center gap-1 font-medium"
                            >
                                <KeyRound size={13} /> Forgot password?
                            </Link>
                            <span className="text-[#71717A] flex items-center gap-1">
                                <ShieldCheck size={13} /> Encrypted
                            </span>
                        </div>
                    </form>
                    )}
                </div>

                <p className="text-center text-xs text-white/70 mt-6 font-mono">
                    gtw.co.in · Trusted by 2800+ taxpayers
                </p>

                <InstallAppButton variant="login" />

                <div className="text-center text-[10px] text-white/50 mt-3 space-x-3">
                    <a href="mailto:support@gtw.co.in" className="hover:text-[#C5A059]">support@gtw.co.in</a>
                    <span>·</span>
                    <Link to="/privacy" data-testid="login-link-privacy" className="hover:text-[#C5A059]">Privacy Policy</Link>
                    <span>·</span>
                    <Link to="/terms" data-testid="login-link-terms" className="hover:text-[#C5A059]">Terms</Link>
                    <span>·</span>
                    <Link to="/refunds" data-testid="login-link-refunds" className="hover:text-[#C5A059]">Refunds</Link>
                    <span>·</span>
                    <Link to="/contact" data-testid="login-link-contact" className="hover:text-[#C5A059]">Contact</Link>
                </div>

                <div data-testid="login-copyright" className="text-center text-[10px] text-white/40 mt-4 leading-relaxed px-4">
                    © 2026 gtw.co.in. All rights reserved. gtw.co.in is owned and operated by GHATOSKASH TAX WIZARDS PRIVATE LIMITED (Tax Solution is the brand name).
                </div>
            </div>
        </div>
    );
}
